import { useEffect, useRef, useState } from 'react';
import { createMorphEngine, type MorphHandle } from '@/lib/rack/morphEngine';
import { channels, ACCENT_HEX } from '@/data/channels';

/**
 * 首屏 —— 整页唯一的大动作。
 *
 * 同一组 126 个点在三种构型之间形变：骨架 / 编排图 / 权益曲线。点不生不灭，
 * 只是重新排列——三个方向底下是同一套解算，这句话被字面地画了出来。
 * 其余版面一律保持安静，boldness 只花在这一处。
 *
 * 版面基准是那根量尺（gutterRef）：画布铺满视口，正文却是居中定宽的，图形、
 * 右下角铭牌、通道按钮全部读量尺定位，超宽屏上才不会各自贴到视口边上去。
 */
export default function MorphHero() {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<MorphHandle | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!canvasRef.current || !stageRef.current) return;
    const engine = createMorphEngine(canvasRef.current, {
      stage: stageRef.current,
      gutter: gutterRef.current,
      onChannel: setActive,
    });
    engineRef.current = engine;
    return () => engine.destroy();
  }, []);

  const pick = (i: number) => { engineRef.current?.setChannel(i); setActive(i); };

  return (
    <section
      ref={stageRef}
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-rack-ground"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />

      {/* 量尺：和正文同宽的空元素，画布靠读它的矩形来对齐。改容器宽度时这里跟着改一处即可 */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto max-w-[1180px] px-6 sm:px-10"
        aria-hidden="true"
      >
        <div ref={gutterRef} className="h-px w-full" />
      </div>

      {/* 胶片颗粒：让纯黑底不那么"数字" */}
      <div
        className="pointer-events-none absolute inset-0 opacity-50 mix-blend-soft-light"
        aria-hidden="true"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1180px] flex-col justify-start px-6 pb-32 pt-24 sm:justify-center sm:px-10 sm:pt-28">
        <div className="max-w-[560px]">
          <div className="mb-5 h-px w-9 bg-rack-amber" />
          <p className="mb-6 font-readout text-[10px] uppercase tracking-[0.34em] text-rack-bone/30">
            独立开发者 · Solo Builder
          </p>

          <h1 className="font-songti text-[clamp(2.1rem,7.2vw,3.4rem)] font-semibold leading-[1.22] tracking-[0.16em] text-rack-bone">
            阳仔AI
            <br />
            独立<em className="not-italic text-rack-amber">开发</em>者
          </h1>

          <p className="mt-6 max-w-[30ch] text-[13px] leading-[1.9] text-rack-bone/55">
            不外包、不转手、不甩锅。从第一版原型到线上运维，同阳仔AI负责到底。
          </p>

          <dl className="mt-7 space-y-2 text-[12.5px] leading-relaxed">
            {[
              ['交付', '需求 → 方案 → 上线 → 运维，全链路'],
              ['方向', '视觉动捕 / 智能体 / 量化'],
              ['合作', 'YangZaiAITech'],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-4">
                <dt className="w-[44px] shrink-0 pt-px font-readout text-[10px] tracking-[0.14em] text-rack-bone/28">
                  {k}
                </dt>
                <dd className="text-rack-bone/80">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* 通道选择器：并联的三路输入，不是三个步骤。跟正文同容器，才和大字左缘对齐 */}
      <div className="absolute inset-x-0 bottom-7 z-20 mx-auto max-w-[1180px] px-6 sm:px-10">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="选择展示通道">
        {channels.map((c, i) => {
          const on = i === active;
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={on}
              onClick={() => pick(i)}
              onMouseEnter={() => { engineRef.current?.setChannel(i); engineRef.current?.setLocked(true); setActive(i); }}
              onMouseLeave={() => engineRef.current?.setLocked(false)}
              className="rounded-sm border px-3.5 py-1.5 text-[11px] tracking-[0.06em] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-rack-amber focus-visible:ring-offset-2 focus-visible:ring-offset-rack-ground"
              style={
                on
                  ? { background: ACCENT_HEX[c.accent], borderColor: 'transparent', color: '#0B0A08', fontWeight: 600 }
                  : { background: 'transparent', borderColor: 'rgba(230,224,212,.16)', color: 'rgba(230,224,212,.46)' }
              }
            >
              <span
                className="mr-2 font-readout text-[9px]"
                style={{ color: on ? 'rgba(11,10,8,.5)' : 'rgba(230,224,212,.26)' }}
              >
                {c.no}
              </span>
              {c.name}
            </button>
          );
        })}
        </div>
      </div>
    </section>
  );
}
